import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Check, Edit, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function CategoriesManager() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [editing, setEditing] = useState(null);
  const [editName, setEditName] = useState("");

  const load = () => api.get("/categories").then((r) => setCats(r.data)).catch((e) => toast.error(errorMessage(e)));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try { await api.post("/admin/categories", { name }); setName(""); toast.success("Category added"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const save = async (id) => {
    try { await api.put(`/admin/categories/${id}`, { name: editName }); setEditing(null); toast.success("Category renamed"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Tutorials in it will become uncategorized.`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Category deleted"); load(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14" data-testid="categories-manager">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-indigo-700 mb-6" data-testid="categories-back-link"><ArrowLeft className="w-4 h-4" /> Back to tutorials</Link>
      <p className="eyebrow">Admin panel</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-2 mb-8">Categories</h1>

      <form onSubmit={add} className="flex gap-2 mb-8">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="New category name" className="h-11 rounded-full bg-white" data-testid="category-name-input" />
        <Button type="submit" className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="category-add-button"><Plus className="w-4 h-4" /> Add</Button>
      </form>

      <ul className="rounded-3xl bg-white border border-slate-200 divide-y divide-slate-100" data-testid="categories-list">
        {cats.length === 0 && <li className="px-6 py-10 text-center text-slate-500">No categories yet.</li>}
        {cats.map((c) => (
          <li key={c.id} className="flex items-center gap-3 px-6 py-4" data-testid={`category-row-${c.id}`}>
            {editing === c.id ? (
              <>
                <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-10 rounded-xl" autoFocus data-testid={`category-edit-input-${c.id}`} />
                <Button size="icon" variant="ghost" onClick={() => save(c.id)} aria-label="Save" data-testid={`category-save-${c.id}`}><Check className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => setEditing(null)} aria-label="Cancel"><X className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <div className="flex-1">
                  <p className="font-medium text-slate-900">{c.name}</p>
                  <p className="text-xs text-slate-500">{c.tutorial_count} published tutorial{c.tutorial_count === 1 ? "" : "s"}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => { setEditing(c.id); setEditName(c.name); }} aria-label="Rename" data-testid={`category-edit-${c.id}`}><Edit className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => remove(c)} aria-label="Delete" data-testid={`category-delete-${c.id}`}><Trash2 className="w-4 h-4" /></Button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
